import React from "react";
import { observer } from "mobx-react";
import { Box, Paper } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { Typography } from "@mui/material";
import { graphqlToDateTime } from "../../../../common/utils";

export type MusicalWorkMiniFormProps = {
  data: any;
  backgroundColor?: string;
  visibleFields?: string[];
};

export const MusicalWorkMiniForm: React.FC<MusicalWorkMiniFormProps> = observer(
  ({ data, backgroundColor = "background.paper", visibleFields }) => {
    const rootStore = useRootStore();
    const { langStore } = rootStore;

    const shouldShowField = (fieldName: string) => {
      if (!visibleFields || visibleFields.length === 0) return true;
      return visibleFields.includes(fieldName);
    };

    if (!data) return null;

    return (
      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          backgroundColor,
          p: 1.5,
          width: "fit-content",
        }}
      >
        {shouldShowField("title") && (
          <Box sx={{ mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {langStore.MusicalWorkTitleLabel}
            </Typography>
            <Typography variant="body2" color="text.primary">
              {data?.title || ""}
            </Typography>
          </Box>
        )}
        {shouldShowField("musicalGenre") && (
          <Box sx={{ mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {langStore.MusicalWorkMusicalGenreLabel}
            </Typography>
            <Typography variant="body2" color="text.primary">
              {data?.showMusicalGenre?.displayId || ""}
            </Typography>
          </Box>
        )}
        {shouldShowField("alternativeTitles") && (
          <Box sx={{ mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {langStore.MusicalWorkAlternativeTitlesLabel}
            </Typography>
            <Typography
              variant="body2"
              color="text.primary"
              sx={{ whiteSpace: "pre-wrap" }}
            >
              {(data?.alternativeTitles || []).join("\n")}
            </Typography>
          </Box>
        )}
        {shouldShowField("authors") && (
          <Box sx={{ mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {langStore.MusicalWorkAuthorsLabel}
            </Typography>
            <Typography
              variant="body2"
              color="text.primary"
              sx={{ whiteSpace: "pre-wrap" }}
            >
              {(data?.showAuthors || [])
                .map((x: any) => x.displayId || "")
                .join("\n")}
            </Typography>
          </Box>
        )}
        {shouldShowField("notes") && (
          <Box sx={{ mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {langStore.MusicalWorkNotesLabel}
            </Typography>
            <Typography variant="body2" color="text.primary">
              {data?.notes || ""}
            </Typography>
          </Box>
        )}

        {shouldShowField("performers") && (
          <Box sx={{ mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {langStore.MusicalWorkPerformersLabel}
            </Typography>
            <Typography
              variant="body2"
              color="text.primary"
              sx={{ whiteSpace: "pre-wrap" }}
            >
              {(data?.showPerformers || [])
                .map((x: any) => x.displayId || "")
                .join("\n")}
            </Typography>
          </Box>
        )}
        {shouldShowField("cmsId") && (
          <Box sx={{ mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {langStore.MusicalWorkCmsIdLabel}
            </Typography>
            <Typography variant="body2" color="text.primary">
              {data?.cmsId || ""}
            </Typography>
          </Box>
        )}
        {shouldShowField("territories") && (
          <Box sx={{ mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {langStore.MusicalWorkTerritoriesLabel}
            </Typography>
            <Typography
              variant="body2"
              color="text.primary"
              sx={{ whiteSpace: "pre-wrap" }}
            >
              {(data?.showTerritories || [])
                .map((x: any) => x.displayId || "")
                .join("\n")}
            </Typography>
          </Box>
        )}
        {shouldShowField("iswc") && (
          <Box sx={{ mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {langStore.MusicalWorkIswcLabel}
            </Typography>
            <Typography variant="body2" color="text.primary">
              {data?.iswc || ""}
            </Typography>
          </Box>
        )}
        {shouldShowField("workNumber") && (
          <Box sx={{ mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {langStore.MusicalWorkWorkNumberLabel}
            </Typography>
            <Typography variant="body2" color="text.primary">
              {data?.workNumber ?? 0}
            </Typography>
          </Box>
        )}

        {shouldShowField("phonograms") && (
          <Box sx={{ mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {langStore.MusicalWorkPhonogramsLabel}
            </Typography>
            <Typography
              variant="body2"
              color="text.primary"
              sx={{ whiteSpace: "pre-wrap" }}
            >
              {(data?.showPhonograms || [])
                .map((x: any) => x.displayId || "")
                .join("\n")}
            </Typography>
          </Box>
        )}
        {shouldShowField("originalLyrics") && (
          <Box sx={{ mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {langStore.MusicalWorkOriginalLyricsLabel}
            </Typography>
            <Typography variant="body2" color="text.primary">
              {data?.originalLyrics || ""}
            </Typography>
          </Box>
        )}

        {shouldShowField("contractObservation") && (
          <Box sx={{ mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {langStore.MusicalWorkContractObservationLabel}
            </Typography>
            <Typography variant="body2" color="text.primary">
              {data?.contractObservation || ""}
            </Typography>
          </Box>
        )}

        {shouldShowField("createdAt_") && (
          <Box sx={{ mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {langStore.MusicalWorkCreatedAt_Label}
            </Typography>
            <Typography variant="body2" color="text.primary">
              {data?.createdAt_ ? graphqlToDateTime(data.createdAt_) : ""}
            </Typography>
          </Box>
        )}
        {shouldShowField("lastUpdatedAt_") && (
          <Box sx={{ mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {langStore.MusicalWorkLastUpdatedAt_Label}
            </Typography>
            <Typography variant="body2" color="text.primary">
              {data?.lastUpdatedAt_
                ? graphqlToDateTime(data.lastUpdatedAt_)
                : ""}
            </Typography>
          </Box>
        )}
      </Paper>
    );
  }
);
